import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Store, RefreshCw, AlertTriangle, Check, X, Undo2, Banknote, ListFilter,
    HelpCircle, PencilLine, Info, ChevronRight, ChevronDown, ShieldCheck, Calendar,
} from 'lucide-react';
import { Moeda, brl, VERDE } from './trocoFormato';
import { MES_MINIMO, noMesISO } from './trocoFiltros';
import { TrocoSituacaoBadge, SITUACAO, rotuloSituacao } from './trocoStatus';
import useOrdenacaoTabela from '../useOrdenacaoTabela';
import { Paginacao, ITENS_POR_PAGINA } from './trocoPaginacao';

// ============================================================================
// CONFERÊNCIA — UMA TABELA, TRÊS SITUAÇÕES (reescrita 2026-07-30)
// ============================================================================
// A primeira versão desta tela eram QUATRO blocos de cards. Apresentada ao
// Financeiro, o veredito foi "confuso" — e com razão: é um fluxo só, partido em
// quatro lugares, obrigando a percorrer a tela inteira para saber o que precisa
// de ação. Virou uma TABELA, no mesmo formato do Controle (que eles aprovaram),
// com três situações:
//
//   Aguardando Confirmação com Financeiro → precisa do FINANCEIRO
//       Junta o que a tela antiga separava sem motivo: o depósito que o caixa
//       registrou e o motor casou (proposta) E o depósito que a loja já
//       declarou. Nos dois casos a pergunta é a mesma — "o dinheiro chegou?" —
//       e quem responde é a mesma pessoa.
//   Aguardando Depósito → precisa da LOJA (troco recebido, não depositado)
//   Depósito em Aberto  → o caixa registrou e não há troco em aberto para casar
//
// ⚠️ UM CLIQUE, NÃO DOIS (para o Financeiro). Como proposta e declarado caem na
// MESMA situação, aceitar uma proposta e depois confirmá-la deixaria a linha no
// mesmo lugar pedindo um segundo clique — exatamente a confusão que a reescrita
// veio resolver. Quando quem clica é o Financeiro, o depósito nasce já
// confirmado (o caixa registrou, e o clique dele É a conferência). O gerente
// nunca confirma sozinho: o backend ignora o pedido e grava 'Depositado'.

const TEAL = '#0f766e';

const card = { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px' };
const inputStyle = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' };
const btn = (bg, cor = '#fff') => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 13px',
    backgroundColor: bg, color: cor, border: 'none', borderRadius: '7px',
    fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
});
const btnSec = { ...btn('#fff', '#475569'), border: '1px solid #cbd5e1' };
const th = { padding: '13px 14px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', fontWeight: '700' };
const thOrd = { ...th, cursor: 'pointer', userSelect: 'none' };
const td = { padding: '13px 14px', fontSize: '13px', color: '#475569', verticalAlign: 'top' };

// Tipos das colunas ordenáveis (ver useOrdenacaoTabela). Fora do componente para
// a identidade ser estável. `data` é TEXTO porque chega em ISO, que já ordena
// cronologicamente como string.
const TIPOS_ORDENACAO = {
    _situacao: 'texto', loja: 'texto', data: 'texto',
    lacre: 'texto', banana: 'texto', valor: 'numero',
};

const FILTROS_SITUACAO = [
    { valor: '', rotulo: 'Todas as situações' },
    { valor: SITUACAO.CONFIRMACAO, rotulo: rotuloSituacao(SITUACAO.CONFIRMACAO) },
    { valor: SITUACAO.DEPOSITO, rotulo: rotuloSituacao(SITUACAO.DEPOSITO) },
    { valor: SITUACAO.EM_ABERTO, rotulo: rotuloSituacao(SITUACAO.EM_ABERTO) },
];

const dataBR = (iso) => (iso ? String(iso).split('-').reverse().join('/') : '—');

const FinanceiroTrocoConferencia = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const perfil = user?.perfil || '';
    const isGerente = perfil === 'Gerente' || perfil === 'Gerente Plantao';
    // ⚠️ `Operacional` (28/09/2026): acesso total ao Financeiro (nível Admin).
    const podeDepositar = ['Admin', 'Gerente', 'Gerente Plantao', 'Financeiro', 'Operacional'].includes(perfil);
    const podeConfirmar = ['Admin', 'Financeiro', 'Operacional'].includes(perfil);

    const [lojas, setLojas] = useState([]);
    const [lojaSelecionada, setLojaSelecionada] = useState(isGerente && user.loja ? user.loja : '');
    const [situacaoFiltro, setSituacaoFiltro] = useState('');
    // ⚠️ COMPETÊNCIA VAZIA POR PADRÃO — ao contrário do Controle, que abre no mês
    // corrente. Esta tela é FILA DE TRABALHO, e abrir num mês esconderia
    // exatamente o que está atrasado: hoje há troco de JUNHO aguardando depósito
    // em ANV e RDP, que sumiria num padrão "mês corrente". É a mesma regra já
    // registrada no trocoFiltros.js (decisão Julia, 2026-07-28).
    const [mes, setMes] = useState('');
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [pagina, setPagina] = useState(1);
    const [expandida, setExpandida] = useState(null);   // chave da linha aberta

    const [manual, setManual] = useState(null);
    const [acerto, setAcerto] = useState(null);

    const auth = useCallback(() => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
    }, []);

    useEffect(() => {
        fetch('/api/troco/lojas', auth())
            .then(r => r.json())
            .then(d => { if (Array.isArray(d)) setLojas(d); })
            .catch(err => console.error('Erro ao carregar lojas:', err));
    }, [auth]);

    const carregar = useCallback(() => {
        setCarregando(true);
        setErro('');
        const p = lojaSelecionada ? `?loja=${encodeURIComponent(lojaSelecionada)}` : '';
        fetch(`/api/troco/conferencia${p}`, auth())
            .then(r => r.json())
            .then(d => { if (d?.error) setErro(d.error); else setDados(d); })
            .catch(() => setErro('Erro de conexão ao carregar a conferência.'))
            .finally(() => setCarregando(false));
    }, [lojaSelecionada, auth]);

    useEffect(() => { carregar(); }, [carregar]);

    // ------------------------------------------------------------------------
    // As quatro fontes da API viram UMA lista de linhas, com situação.
    // ------------------------------------------------------------------------
    const linhas = useMemo(() => {
        if (!dados) return [];
        const l = [];

        // Proposta do caixa — o motor casou, falta alguém confirmar.
        (dados.propostas || []).forEach(p => l.push({
            chave: `prop-${p.loja}-${p.pdv.envelope}`,
            _situacao: SITUACAO.CONFIRMACAO,
            tipo: 'proposta',
            loja: p.loja,
            data: p.pdv.data,
            // O lacre de um depósito é o dos trocos que ele quita — pode ser mais de um.
            lacre: p.itens.map(i => i.lacre).filter(Boolean).join(', '),
            banana: p.pdv.envelope,
            valor: p.total,
            proposta: p,
        }));

        // Já declarado pela loja — espera o Financeiro.
        (dados.aguardandoConfirmacao || []).forEach(m => l.push({
            chave: `mov-${m.ID}`,
            _situacao: SITUACAO.CONFIRMACAO,
            tipo: m.TIPO === 'Devolucao' ? 'devolucao' : 'declarado',
            loja: m.LOJA,
            data: m.DATA_MOVIMENTO,
            lacre: '',
            banana: m.NUMERO_BANANA || '',
            valor: parseFloat(m.VALOR),
            movimento: m,
        }));

        // Troco recebido e não depositado — ação da loja.
        (dados.trocosAbertos || []).forEach(t => l.push({
            chave: `troco-${t.ID}`,
            _situacao: SITUACAO.DEPOSITO,
            tipo: 'troco',
            loja: t.loja,
            data: t.DATA_TROCO,
            lacre: t.NUMERO_LACRE || '',
            banana: '',
            valor: parseFloat(t.aberto),
            troco: t,
        }));

        // O caixa registrou e não há troco em aberto para casar.
        (dados.semProposta || []).forEach((s, i) => l.push({
            chave: `aberto-${s.loja}-${s.envelope}-${i}`,
            _situacao: SITUACAO.EM_ABERTO,
            tipo: 'semTroco',
            loja: s.loja,
            data: s.data,
            lacre: '',
            banana: s.envelope,
            valor: parseFloat(s.valor),
        }));

        return l;
    }, [dados]);

    // ⚠️ O recorte por mês é aplicado no RESULTADO, nunca antes do motor. Filtrar
    // as fontes por competência quebraria o casamento: um troco de junho pode ser
    // quitado por um depósito de julho, e os dois precisam estar na mesa para o
    // motor propor. É a mesma regra do /conferencia-pdv no backend.
    //
    // Cada linha filtra pela data do FATO que ela representa — o recolhimento no
    // caixa, o depósito declarado ou o envio do troco. É a mesma data da coluna.
    const filtradas = useMemo(
        () => linhas.filter(l =>
            (!situacaoFiltro || l._situacao === situacaoFiltro) && noMesISO(l.data, mes)
        ),
        [linhas, situacaoFiltro, mes]
    );

    const { dadosOrdenados, ordenar, indicador } = useOrdenacaoTabela(filtradas, TIPOS_ORDENACAO);

    useEffect(() => { setPagina(1); }, [lojaSelecionada, situacaoFiltro, mes]);

    const totalPaginas = Math.max(1, Math.ceil(dadosOrdenados.length / ITENS_POR_PAGINA));
    const paginaSegura = Math.min(pagina, totalPaginas);
    const daPagina = dadosOrdenados.slice((paginaSegura - 1) * ITENS_POR_PAGINA, paginaSegura * ITENS_POR_PAGINA);

    // Contadores por situação — o resumo que diz onde está o trabalho.
    // ⚠️ Contam sobre o mês filtrado (mas NÃO sobre a situação escolhida, que é
    // justamente o que esses números ajudam a escolher). Contar o total geral
    // faria o seletor prometer "7" e a tabela mostrar 4.
    const contagem = useMemo(() => {
        const doMes = linhas.filter(l => noMesISO(l.data, mes));
        return {
            [SITUACAO.CONFIRMACAO]: doMes.filter(l => l._situacao === SITUACAO.CONFIRMACAO).length,
            [SITUACAO.DEPOSITO]: doMes.filter(l => l._situacao === SITUACAO.DEPOSITO).length,
            [SITUACAO.EM_ABERTO]: doMes.filter(l => l._situacao === SITUACAO.EM_ABERTO).length,
        };
    }, [linhas, mes]);

    // Há trabalho pendente FORA do mês filtrado? É o que impede a tela de dizer
    // "nada pendente 🎉" enquanto existe troco atrasado escondido pelo filtro —
    // numa fila de trabalho, esse falso alívio é pior que não ter filtro.
    const escondidasPeloMes = mes ? linhas.length - linhas.filter(l => noMesISO(l.data, mes)).length : 0;

    // --- Ações ---------------------------------------------------------------
    const enviarDeposito = async (corpo) => {
        setSalvando(true);
        try {
            const res = await fetch('/api/troco/deposito', { method: 'POST', ...auth(), body: JSON.stringify(corpo) });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) { alert(d.error || 'Erro ao registrar o depósito.'); return false; }
            setManual(null);
            carregar();
            return true;
        } catch {
            alert('Erro de conexão com o servidor.');
            return false;
        } finally { setSalvando(false); }
    };

    const aceitarProposta = (p) => enviarDeposito({
        loja: p.loja,
        numeroBanana: p.pdv.envelope,
        valor: p.total,
        data: p.pdv.data,
        itens: p.itens.map(i => ({ idTroco: i.idTroco, valorAplicado: i.valorAplicado })),
        residuo: p.residuo,
        origem: 'PDV',
        // Quando é o Financeiro, o clique dele já é a conferência (o backend
        // ignora este pedido vindo de gerente).
        confirmar: podeConfirmar,
    });

    const confirmarMovimento = async (id) => {
        setSalvando(true);
        try {
            const res = await fetch(`/api/troco/movimento/${id}/confirmar`, { method: 'PUT', ...auth() });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) alert(d.error || 'Erro ao confirmar.'); else carregar();
        } catch { alert('Erro de conexão com o servidor.'); }
        finally { setSalvando(false); }
    };

    // Abre o "Lançar acerto". Com `linha`, o acerto nasce COLADO num recolhimento
    // do caixa (situação Depósito em Aberto) e leva a BANANA junto — é ela que
    // faz a linha sair da Conferência, porque o motor só para de repropor um
    // recolhimento quando algum movimento já gravou aquele número. Sem `linha`,
    // é o acerto avulso de sempre: sem banana, sem vínculo com o PDV.
    const abrirAcerto = (linha = null) => setAcerto({
        loja: linha?.loja || lojaSelecionada || '',
        // Avulso, o caso comum é a devolução ao escritório. Vindo de um
        // recolhimento, é correção de saldo: o dinheiro FOI no malote, o que
        // faltou foi o lançamento do troco — nada a devolver.
        tipo: linha ? 'Correcao' : 'Devolucao',
        sentido: 'Saida',
        valor: linha ? String(linha.valor) : '',
        data: linha?.data || '',
        motivo: '',
        banana: linha?.banana || '',
    });

    const enviarAcerto = async () => {
        setSalvando(true);
        try {
            const corpo = {
                loja: acerto.loja, tipo: acerto.tipo, sentido: acerto.sentido,
                valor: acerto.valor, data: acerto.data, motivo: acerto.motivo,
                // Só viaja quando existe: o backend recusa banana que o PDV não
                // conhece, e acerto avulso não tem nenhuma.
                numeroBanana: acerto.banana || undefined,
            };
            const res = await fetch('/api/troco/ajuste', { method: 'POST', ...auth(), body: JSON.stringify(corpo) });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) { alert(d.error || 'Erro ao lançar o acerto.'); return; }
            setAcerto(null);
            carregar();
        } catch { alert('Erro de conexão com o servidor.'); }
        finally { setSalvando(false); }
    };

    // --- Lançamento manual ---------------------------------------------------
    // ⚠️ Precisa de useMemo: `dados?.trocosAbertos || []` cria um array NOVO a
    // cada render, e isso invalidaria o useMemo de baixo em todo render — que é
    // o oposto do que ele existe para fazer.
    const trocosAbertos = useMemo(() => dados?.trocosAbertos || [], [dados]);
    const somaManual = useMemo(() => {
        if (!manual) return 0;
        return trocosAbertos.filter(t => manual.selecionados.has(t.ID))
            .reduce((s, t) => s + parseFloat(t.aberto), 0);
    }, [manual, trocosAbertos]);

    const residuoManual = useMemo(() => {
        if (!manual) return 0;
        const v = parseFloat(String(manual.valor).replace(',', '.')) || 0;
        return Math.round((v - somaManual) * 100) / 100;
    }, [manual, somaManual]);

    const abrirManual = (troco) => setManual({
        loja: troco ? troco.loja : (lojaSelecionada || trocosAbertos[0]?.loja || ''),
        selecionados: new Set(troco ? [troco.ID] : []),
        banana: '', valor: troco ? String(troco.aberto) : '', data: '', motivo: '',
    });

    return (
        <div>
            {/* ------------------------------------------------------- CABEÇALHO */}
            <div style={{ ...card, padding: '16px 24px', marginBottom: '18px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Store size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px' }} />
                    <select
                        value={lojaSelecionada}
                        onChange={(e) => setLojaSelecionada(e.target.value)}
                        style={{ ...inputStyle, paddingLeft: '36px', backgroundColor: isGerente ? '#f1f5f9' : '#fff' }}
                        disabled={isGerente}
                    >
                        <option value="">Todas as Lojas</option>
                        {isGerente && !lojas.find(l => l.NOME_LOJA === user.loja) && (
                            <option value={user.loja}>{user.loja}</option>
                        )}
                        {lojas.map((l, i) => <option key={i} value={l.NOME_LOJA}>{l.NOME_LOJA}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <ListFilter size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px' }} />
                    <select value={situacaoFiltro} onChange={(e) => setSituacaoFiltro(e.target.value)} style={{ ...inputStyle, paddingLeft: '36px' }}>
                        {FILTROS_SITUACAO.map(f => (
                            <option key={f.valor} value={f.valor}>
                                {f.rotulo}{f.valor ? ` (${contagem[f.valor] || 0})` : ''}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Competência (MM-AAAA). Sem mês = tudo, que é o padrão desta
                    tela por ser fila de trabalho — ver a nota no estado `mes`. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={16} color="#94a3b8" />
                    <input type="month" min={MES_MINIMO} value={mes} onChange={(e) => setMes(e.target.value)} style={inputStyle} />
                    {mes && (
                        <button onClick={() => setMes('')} title="Ver todos os períodos"
                            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}>
                            limpar
                        </button>
                    )}
                </div>

                <button onClick={carregar} style={btnSec} disabled={carregando}>
                    <RefreshCw size={14} /> {carregando ? 'Atualizando…' : 'Atualizar'}
                </button>

                <span style={{ flex: 1 }} />

                {podeDepositar && trocosAbertos.length > 0 && (
                    <button onClick={() => abrirManual(null)} style={btn(TEAL)}>
                        <Banknote size={14} /> Lançar depósito
                    </button>
                )}
                {podeConfirmar && (
                    <button onClick={() => abrirAcerto()} style={btnSec}>
                        <PencilLine size={14} /> Lançar acerto
                    </button>
                )}
            </div>

            {erro && (
                <div style={{ display: 'flex', gap: '10px', padding: '14px 16px', borderRadius: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', marginBottom: '18px' }}>
                    <AlertTriangle size={18} color="#b91c1c" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', color: '#7f1d1d' }}>{erro}</span>
                </div>
            )}

            {/* O ETL é D-1: dizer isso evita o gerente achar que o sistema "perdeu"
                o depósito que ele fez hoje. */}
            {dados?.ultimoDiaPdv && (
                <div style={{ display: 'flex', gap: '9px', padding: '11px 16px', borderRadius: '10px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: '18px' }}>
                    <Info size={15} color="#64748b" style={{ flexShrink: 0, marginTop: '1px' }} />
                    <span style={{ fontSize: '12.5px', color: '#64748b', lineHeight: 1.5 }}>
                        O registro do caixa está carregado até <strong>{dataBR(dados.ultimoDiaPdv)}</strong>. Depósito
                        feito depois disso ainda não aparece aqui — <strong>lance na mão</strong>, não espere.
                    </span>
                </div>
            )}

            {/* ---------------------------------------------------------- TABELA */}
            <div style={{ ...card, overflow: 'hidden' }}>
                <div style={{ padding: '18px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <ShieldCheck size={19} color={TEAL} />
                    <h3 style={{ margin: 0, fontSize: '17px', color: '#0f172a' }}>Conferência de Depósitos</h3>
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: '12px', color: '#64748b' }}>{dadosOrdenados.length} registro(s)</span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '1000px' }}>
                        <thead style={{ backgroundColor: '#f8fafc', fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>
                            <tr>
                                <th style={{ ...th, width: '30px' }} />
                                <th onClick={() => ordenar('_situacao')} style={thOrd}>Situação{indicador('_situacao')}</th>
                                <th onClick={() => ordenar('loja')} style={thOrd}>Loja{indicador('loja')}</th>
                                <th onClick={() => ordenar('data')} style={thOrd}>Data{indicador('data')}</th>
                                <th onClick={() => ordenar('lacre')} style={thOrd}>Nº Lacre{indicador('lacre')}</th>
                                <th onClick={() => ordenar('banana')} style={thOrd}>Nº Banana{indicador('banana')}</th>
                                <th onClick={() => ordenar('valor')} style={{ ...thOrd, textAlign: 'right' }}>Valor{indicador('valor')}</th>
                                <th style={th}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dadosOrdenados.length === 0 && !carregando ? (
                                <tr>
                                    <td colSpan="8" style={{ padding: '34px', textAlign: 'center', color: '#64748b' }}>
                                        {escondidasPeloMes > 0 ? (
                                            <>
                                                Nada pendente <strong>neste período</strong> — mas há{' '}
                                                <strong>{escondidasPeloMes} pendência(s) em outros meses</strong>.
                                                <br />
                                                <button onClick={() => setMes('')} style={{ marginTop: '10px', background: 'none', border: 'none', color: TEAL, cursor: 'pointer', fontSize: '13px', fontWeight: '700', textDecoration: 'underline' }}>
                                                    ver todos os períodos
                                                </button>
                                            </>
                                        ) : 'Nada pendente de conferência. 🎉'}
                                    </td>
                                </tr>
                            ) : daPagina.map(l => {
                                const temDetalhe = l.tipo === 'proposta' || (l.movimento?.itens?.length > 0);
                                const aberta = expandida === l.chave;
                                return (
                                    <React.Fragment key={l.chave}>
                                        <tr style={{ borderBottom: aberta ? 'none' : '1px solid #f1f5f9' }}>
                                            <td style={{ ...td, paddingRight: 0 }}>
                                                {temDetalhe && (
                                                    <button
                                                        onClick={() => setExpandida(aberta ? null : l.chave)}
                                                        title={aberta ? 'Fechar detalhe' : 'Ver o que este depósito quita'}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex' }}
                                                    >
                                                        {aberta ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                    </button>
                                                )}
                                            </td>
                                            <td style={td}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-start' }}>
                                                    <TrocoSituacaoBadge situacao={l._situacao} />
                                                    {/* Ambiguidade: mais de uma combinação fecha o mesmo
                                                        valor. Escolher no palpite quitaria o troco errado
                                                        com cara de certeza — então a linha avisa. */}
                                                    {l.proposta?.ambiguo && (
                                                        <span title={`Há ${l.proposta.totalCombinacoes} combinações de trocos que somam este valor. Confira antes de confirmar.`}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: '800', color: '#b45309', backgroundColor: '#fef3c7', padding: '2px 7px', borderRadius: '999px' }}>
                                                            <HelpCircle size={11} /> CONFIRA
                                                        </span>
                                                    )}
                                                    {l.tipo === 'devolucao' && (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: '800', color: '#c2410c', backgroundColor: '#ffedd5', padding: '2px 7px', borderRadius: '999px' }}>
                                                            <Undo2 size={11} /> DEVOLUÇÃO
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ ...td, fontWeight: '700', color: '#0f172a' }}>{l.loja}</td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{dataBR(l.data)}</td>
                                            <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{l.lacre || '—'}</td>
                                            <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{l.banana || '—'}</td>
                                            <td style={{ ...td, textAlign: 'right', fontWeight: '700', fontSize: '14px' }}>
                                                <Moeda valor={l.valor} />
                                            </td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                                                    {/* Os dois botões abaixo levam ao MESMO estado (o depósito
                                                        confirmado), então usam a MESMA cor — antes um era teal e
                                                        outro verde, com o mesmo rótulo "Confirmar". */}
                                                    {l.tipo === 'proposta' && podeDepositar && (
                                                        <button onClick={() => aceitarProposta(l.proposta)} disabled={salvando} style={btn(VERDE)}>
                                                            <Check size={13} /> {podeConfirmar ? 'Confirmar' : 'Declarar'}
                                                        </button>
                                                    )}
                                                    {(l.tipo === 'declarado' || l.tipo === 'devolucao') && (
                                                        <>
                                                            {podeConfirmar && (
                                                                <button onClick={() => confirmarMovimento(l.movimento.ID)} disabled={salvando} style={btn(VERDE)}>
                                                                    <Check size={13} /> Confirmar
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                    {l.tipo === 'troco' && podeDepositar && (
                                                        <button onClick={() => abrirManual(l.troco)} disabled={salvando} style={btn(TEAL)}>
                                                            <Banknote size={13} /> Depositar
                                                        </button>
                                                    )}
                                                    {/* O caixa recolheu e não há troco para casar. A tela
                                                        sempre disse o que CONFERIR, mas não oferecia como
                                                        FECHAR — e o acerto lançado pelo botão do topo não
                                                        levava a banana, então a linha voltava para sempre. */}
                                                    {l.tipo === 'semTroco' && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
                                                            <span style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                                                                Confira se há troco pendente de recebimento
                                                            </span>
                                                            {podeConfirmar && (
                                                                <button onClick={() => abrirAcerto(l)} disabled={salvando} style={btnSec}
                                                                    title="Não há troco a receber: lançar o acerto de saldo e encerrar esta pendência">
                                                                    <PencilLine size={13} /> Lançar acerto
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        {/* Detalhe: o que este depósito quita. Fica escondido por
                                            padrão — a tabela responde "o que preciso fazer"; o
                                            detalhe responde "por quê", e só quando perguntado. */}
                                        {aberta && (
                                            <tr style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
                                                <td />
                                                <td colSpan="7" style={{ padding: '4px 14px 14px' }}>
                                                    {(l.proposta?.itens || []).map(it => (
                                                        <div key={it.idTroco} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', fontSize: '12.5px', color: '#475569' }}>
                                                            <span style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{dataBR(it.dataTroco)}</span>
                                                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>lacre {it.lacre || '—'}</span>
                                                            <span style={{ flex: 1 }} />
                                                            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{brl(it.valorAplicado)}</strong>
                                                            {it.parcial && (
                                                                <span style={{ fontSize: '10px', fontWeight: '800', color: '#1d4ed8', backgroundColor: '#dbeafe', padding: '2px 7px', borderRadius: '999px' }}>
                                                                    PARCIAL de {brl(it.valorAberto)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {l.proposta?.residuo > 0 && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0 0', borderTop: '1px dashed #cbd5e1', marginTop: '6px', fontSize: '12.5px' }}>
                                                            <Undo2 size={13} color="#c2410c" />
                                                            <span style={{ color: '#c2410c', fontWeight: '700' }}>
                                                                Devolução ao escritório — o depósito é maior que os trocos em aberto
                                                            </span>
                                                            <span style={{ flex: 1 }} />
                                                            <strong style={{ color: '#c2410c', fontVariantNumeric: 'tabular-nums' }}>{brl(l.proposta.residuo)}</strong>
                                                        </div>
                                                    )}
                                                    {l.proposta?.tipo === 'Parcial' && (
                                                        <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#1e40af' }}>
                                                            O depósito é <strong>menor</strong> que os trocos em aberto: quita os mais antigos
                                                            e deixa o resto pendente. Nenhuma devolução é registrada.
                                                        </p>
                                                    )}
                                                    {l.movimento && (
                                                        <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: '#94a3b8' }}>
                                                            Declarado por {l.movimento.REGISTRADO_POR_NOME || '—'} em {l.movimento.REGISTRADO_EM || '—'}
                                                            {l.movimento.itens?.length > 0 && ` · quita ${l.movimento.itens.length} troco(s)`}
                                                            {l.movimento.MOTIVO ? ` · ${l.movimento.MOTIVO}` : ''}
                                                        </p>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <Paginacao pagina={paginaSegura} totalPaginas={totalPaginas} onChange={setPagina} />
            </div>

            {/* ================================================= MODAL: MANUAL */}
            {manual && (
                <Modal titulo="Lançar depósito" onFechar={() => setManual(null)}>
                    <p style={{ margin: '0 0 16px', fontSize: '12.5px', color: '#64748b', lineHeight: 1.5 }}>
                        Escolha os trocos que este depósito quita e informe a banana. Se o valor depositado for
                        <strong> maior</strong> que os trocos, a diferença vira <strong>Devolução ao Escritório</strong>.
                    </p>

                    <div style={{ maxHeight: '190px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '9px', padding: '4px 12px', marginBottom: '14px' }}>
                        {trocosAbertos
                            .filter(t => !manual.loja || t.loja === manual.loja)
                            .map(t => (
                                <label key={t.ID} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', borderBottom: '1px solid #f1f5f9', fontSize: '13px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={manual.selecionados.has(t.ID)}
                                        onChange={() => {
                                            const s = new Set(manual.selecionados);
                                            if (s.has(t.ID)) s.delete(t.ID); else s.add(t.ID);
                                            setManual({ ...manual, selecionados: s, loja: t.loja });
                                        }}
                                    />
                                    <span style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{dataBR(t.DATA_TROCO)}</span>
                                    <span style={{ color: '#475569' }}>lacre {t.NUMERO_LACRE || '—'}</span>
                                    <span style={{ flex: 1 }} />
                                    <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{brl(t.aberto)}</strong>
                                </label>
                            ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                        <Campo label="Nº da banana">
                            <input value={manual.banana} onChange={(e) => setManual({ ...manual, banana: e.target.value })} style={{ ...inputStyle, width: '100%' }} placeholder="144495955" />
                        </Campo>
                        <Campo label="Data do depósito">
                            <input type="date" value={manual.data} onChange={(e) => setManual({ ...manual, data: e.target.value })} style={{ ...inputStyle, width: '100%' }} />
                        </Campo>
                    </div>

                    <Campo label="Valor total depositado">
                        <input value={manual.valor} onChange={(e) => setManual({ ...manual, valor: e.target.value })} style={{ ...inputStyle, width: '100%' }} placeholder="0,00" inputMode="decimal" />
                    </Campo>

                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '9px', padding: '12px 14px', margin: '14px 0', fontSize: '13px' }}>
                        <Conta label="Trocos selecionados" valor={somaManual} />
                        {residuoManual > 0 && <Conta label="Devolução ao escritório" valor={residuoManual} cor="#c2410c" />}
                        {residuoManual < 0 && (
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', color: '#b91c1c', fontSize: '12.5px' }}>
                                <AlertTriangle size={15} style={{ flexShrink: 0 }} />
                                O valor depositado é <strong>menor</strong> que os trocos escolhidos — o sistema vai quitar só parte deles.
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button onClick={() => setManual(null)} style={btnSec} disabled={salvando}>Cancelar</button>
                        <button
                            disabled={salvando || !manual.banana || !manual.data || !manual.valor}
                            onClick={() => {
                                const valorTotal = parseFloat(String(manual.valor).replace(',', '.')) || 0;
                                // Rateio FIFO — a mesma regra do motor, para a tela não inventar outra.
                                const escolhidos = trocosAbertos.filter(t => manual.selecionados.has(t.ID));
                                let restante = Math.min(valorTotal, somaManual);
                                const itens = [];
                                for (const t of escolhidos) {
                                    if (restante <= 0) break;
                                    const aplica = Math.min(restante, parseFloat(t.aberto));
                                    itens.push({ idTroco: t.ID, valorAplicado: Math.round(aplica * 100) / 100 });
                                    restante = Math.round((restante - aplica) * 100) / 100;
                                }
                                enviarDeposito({
                                    loja: manual.loja, numeroBanana: manual.banana, valor: valorTotal,
                                    data: manual.data, itens, residuo: Math.max(0, residuoManual),
                                    motivoDevolucao: manual.motivo, origem: 'Manual',
                                    confirmar: podeConfirmar,
                                });
                            }}
                            style={btn(TEAL)}
                        >
                            <Check size={15} /> {salvando ? 'Salvando…' : 'Registrar depósito'}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ================================================= MODAL: ACERTO */}
            {acerto && (
                <Modal titulo="Lançar acerto de operação" onFechar={() => setAcerto(null)}>
                    {/* Veio de um "Depósito em Aberto": a banana viaja junto e é ela
                        que encerra a pendência. Dizer isso aqui é o que impede o
                        operador de achar que ficou faltando um passo. */}
                    {acerto.banana && (
                        <div style={{ display: 'flex', gap: '9px', padding: '11px 14px', borderRadius: '10px', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', marginBottom: '16px' }}>
                            <Banknote size={15} color={TEAL} style={{ flexShrink: 0, marginTop: '1px' }} />
                            <span style={{ fontSize: '12.5px', color: '#115e59', lineHeight: 1.5 }}>
                                Acerto do recolhimento do caixa <strong style={{ fontVariantNumeric: 'tabular-nums' }}>banana {acerto.banana}</strong>{' '}
                                ({acerto.loja}, {dataBR(acerto.data)}). Ao lançar, esta pendência <strong>sai da Conferência</strong>.
                            </span>
                        </div>
                    )}
                    <p style={{ margin: '0 0 16px', fontSize: '12.5px', color: '#64748b', lineHeight: 1.5 }}>
                        <strong>Devolução</strong> = a loja devolveu numerário ao escritório (o dinheiro andou).{' '}
                        <strong>Correção</strong> = acerto do saldo registrado, sem dinheiro se movendo.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <Campo label="Loja">
                            {/* Com banana, a loja está travada: o backend valida o par
                                loja + banana contra o PDV e recusaria outra loja. */}
                            <select value={acerto.loja} disabled={!!acerto.banana}
                                onChange={(e) => setAcerto({ ...acerto, loja: e.target.value })}
                                style={{ ...inputStyle, width: '100%', backgroundColor: acerto.banana ? '#f8fafc' : '#fff', color: acerto.banana ? '#64748b' : 'inherit' }}>
                                <option value="">Selecione…</option>
                                {lojas.map((l, i) => <option key={i} value={l.NOME_LOJA}>{l.NOME_LOJA}</option>)}
                            </select>
                        </Campo>
                        <Campo label="Tipo">
                            <select value={acerto.tipo} onChange={(e) => setAcerto({ ...acerto, tipo: e.target.value })} style={{ ...inputStyle, width: '100%' }}>
                                <option value="Devolucao">Devolução ao escritório</option>
                                <option value="Correcao">Correção de saldo</option>
                            </select>
                        </Campo>
                    </div>
                    {acerto.tipo === 'Correcao' && (
                        <Campo label="A correção…">
                            <select value={acerto.sentido} onChange={(e) => setAcerto({ ...acerto, sentido: e.target.value })} style={{ ...inputStyle, width: '100%' }}>
                                <option value="Saida">Diminui o saldo da loja</option>
                                <option value="Entrada">Aumenta o saldo da loja</option>
                            </select>
                        </Campo>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', margin: '12px 0' }}>
                        <Campo label="Valor">
                            <input value={acerto.valor} onChange={(e) => setAcerto({ ...acerto, valor: e.target.value })} style={{ ...inputStyle, width: '100%' }} placeholder="0,00" inputMode="decimal" />
                        </Campo>
                        <Campo label="Data">
                            <input type="date" value={acerto.data} onChange={(e) => setAcerto({ ...acerto, data: e.target.value })} style={{ ...inputStyle, width: '100%' }} />
                        </Campo>
                    </div>
                    <Campo label="Motivo (obrigatório)">
                        <input value={acerto.motivo} onChange={(e) => setAcerto({ ...acerto, motivo: e.target.value })} style={{ ...inputStyle, width: '100%' }} placeholder="É a única coisa que explica esta linha daqui a seis meses" />
                    </Campo>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' }}>
                        <button onClick={() => setAcerto(null)} style={btnSec} disabled={salvando}>Cancelar</button>
                        <button onClick={enviarAcerto} disabled={salvando || !acerto.loja || !acerto.valor || !acerto.data || !acerto.motivo} style={btn(TEAL)}>
                            <Check size={15} /> {salvando ? 'Salvando…' : 'Lançar acerto'}
                        </button>
                    </div>
                </Modal>
            )}

        </div>
    );
};

// --- Peças de formulário -----------------------------------------------------
const Campo = ({ label, children }) => (
    <label style={{ display: 'block' }}>
        <span style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '5px' }}>{label}</span>
        {children}
    </label>
);

const Conta = ({ label, valor, cor = '#0f172a' }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
        <span style={{ color: '#64748b' }}>{label}</span>
        <strong style={{ color: cor, fontVariantNumeric: 'tabular-nums' }}>{brl(valor)}</strong>
    </div>
);

const Modal = ({ titulo, onFechar, children }) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{titulo}</h3>
                <button onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}>
                    <X size={19} />
                </button>
            </div>
            <div style={{ padding: '22px 24px' }}>{children}</div>
        </div>
    </div>
);

export default FinanceiroTrocoConferencia;
